import { getEnvParameters } from '../shared/env'
import { Page } from '../../types'
import { getParamsDict } from './util'

type ClientPythonApplicationProps = {
    framework: string
    license: string
    needsLicenseFile: boolean
}

const SimpleFramework = (children: string) => <pre>{children}</pre>

const CodePuLP = 
`from pulp import *
import pulp as pl

# Creates the 'prob' variable to contain the problem data
prob = LpProblem("Sample problem", LpMinimize)

# ... build your model

# The problem is solved using Gurobi using the license file. See the
# tips and tricks section for alternative approaches to customize
# the Gurobi environment being used.
prob.solve(pl.GUROBI())

# The status of the solution is printed to the screen
print("Status:", LpStatus[prob.status])
`

const CodePyomo = 
`import pyomo.environ as po
from pyomo.opt import SolverFactory

model = po.ConcreteModel()
opt = SolverFactory("gurobi_direct")
# ... build your model
results = opt.solve(model, tee=True, keepfiles=True, logfile="test2.log")
model.display()
results.write()`

const CodeORTools = `from ortools.linear_solver import pywraplp

solver = pywraplp.Solver.CreateSolver('GUROBI')
# ... build your model
status = solver.Solve()
print(f'Status: {status}')`

const CodeCVXPY = 
`import gurobipy as gp
import cvxpy as cp

with gp.Env() as env:
  problem = cp.Problem(...)
  ...
  problem.solve(solver=cp.GUROBI, env=env)
  print("The optimal value is", problem.value)
  del problem
`


const GurobiPy = ({ license, needsLicenseFile } : { license: string, needsLicenseFile: boolean }) => {
    const env = getEnvParameters(license)
    const dict = getParamsDict(env?.parameters)
    const codeWithout =
`${dict}
with gp.Env(params=params) as env:
    with gp.Model(env=env) as model:
        # Construct your model
        model.optimize()`
    
    const codeWith =
`with gp.Env() as env:
    with gp.Model(env=env) as model:
        # Construct your model
        model.optimize()`
        
    return <>
        {!needsLicenseFile && <h3>Using the license file</h3>}
        <p>When you have a license file stored as described previously, then constructing your Gurobi model is fairly straightforward.
            Gurobi will load the license file automatically as soon as the <code>env</code> variable is initialized.
        </p>
        <pre>{codeWith}</pre>
        {!needsLicenseFile && <>
            <h3>Without the license file</h3>
            <p>If you prefer configuration through code, you may leave out the license file and configure Gurobi as follows. {env?.hint}</p>
            <pre>{codeWithout}</pre>
            </>
        }
    </>
}

const render = ({ framework, license, needsLicenseFile } : ClientPythonApplicationProps): JSX.Element => {
    if(framework) { 
        const bodies: Record<string, () => JSX.Element> = {
            'pyomo': () => SimpleFramework(CodePyomo),
            'pulp': () => SimpleFramework(CodePuLP),
            'ortools': () => SimpleFramework(CodeORTools),
            'cvxpy': () => SimpleFramework(CodeCVXPY)
        }
        return bodies[framework]()
    }

    return <GurobiPy license={license} needsLicenseFile={needsLicenseFile}/>
}

const ClientPythonApplication : Page<ClientPythonApplicationProps> = { key: 'client-python-application', title: 'Write your application', render }

export default ClientPythonApplication